import { defaultMenuBuilders } from '@components/map/contextMenu/builders/default';
import {
  ContextOf,
  FeatureTypeToContextComponentEnum,
  MenuBuilder,
  MenuKey,
  OptionsOf,
} from '@components/map/contextMenu/type';
import { isSeparator } from '@utils/menu.utils';

import { OperationalMapId } from '@/types/map.types';
import { MenuItems, MenuItemValue } from '@/types/menu.types';

import { featureTypeMenuBuilders, featureTypeOptions } from './builders';

function removeUnnecessarySeparator(entries: [MenuKey | string, MenuItemValue][]): [MenuKey | string, MenuItemValue][] {
  const visibleEntries = entries.filter(([, value]) => !('isHidden' in value && value.isHidden));

  const cleaned: [MenuKey | string, MenuItemValue][] = [];
  let lastWasSeparator = false;

  const lastRealIndex = [...visibleEntries].reverse().findIndex(([, v]) => !isSeparator(v));
  const stopIndex = lastRealIndex === -1 ? -1 : visibleEntries.length - 1 - lastRealIndex;

  for (let i = 0; i <= stopIndex; i++) {
    const [key, value] = visibleEntries[i];
    if ('isHidden' in value && value.isHidden) {
      continue;
    }
    if (isSeparator(value)) {
      if (lastWasSeparator || cleaned.length === 0) {
        continue;
      }
      lastWasSeparator = true;
    } else {
      lastWasSeparator = false;
    }
    cleaned.push([key, value]);
  }
  return cleaned;
}

function buildContextMenuOptions<T extends FeatureTypeToContextComponentEnum>(
  builders: MenuBuilder<T>[],
  options: OptionsOf<T>,
  ctx: ContextOf<T>,
): MenuItems {
  const entries: [MenuKey | string, MenuItemValue][] = [];

  for (const builder of builders) {
    if ('separator' in builder && builder.separator) {
      const uniqueSeparatorKey = `__separator__${builder.order}`;
      entries.push([uniqueSeparatorKey, { type: 'separator' }]);
    } else if ('build' in builder && typeof builder.build === 'function') {
      const [menuKey, menuItemOption] = builder.build(ctx);

      if (options[menuKey as keyof typeof options] === false) {
        continue;
      }
      entries.push([menuKey, menuItemOption]);
    }
  }

  return Object.fromEntries(removeUnnecessarySeparator(entries));
}

export function buildContextMenuFromType<T extends FeatureTypeToContextComponentEnum>(
  type: T,
  mapId: OperationalMapId,
  ctx: ContextOf<T>,
): MenuItems {
  const resolver = featureTypeMenuBuilders[type] ?? defaultMenuBuilders;
  const builders: MenuBuilder<T>[] =
    typeof resolver === 'function'
      ? (resolver as (ctx: ContextOf<T>) => MenuBuilder<T>[])(ctx)
      : (resolver as MenuBuilder<T>[]);
  const options = featureTypeOptions[type][mapId];
  return buildContextMenuOptions(builders, options, ctx);
}
